import { 
  Property, 
  Category, 
  PropertySearchParams, 
  PaginatedResponse, 
  ApiResponse,
  PropertyStats,
  Inquiry 
} from '../types';

//const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost/testaaran1/backend/api';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://aaranhome.com/backend/api';
class ApiService {
  private async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${API_BASE_URL}${endpoint}`;
    console.log('Requesting URL:', url);
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
      mode: 'cors',
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      // Log response status and headers for debugging
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers));

      // Handle non-JSON responses
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('Invalid response format');
      }

      const data = await response.json();
      
      if (!response.ok) {
        console.error('API error response:', data);
        throw new Error(data.message || `API request failed with status ${response.status}`);
      }
      
      return data;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Properties API
  async getProperties(params?: PropertySearchParams): Promise<ApiResponse<PaginatedResponse<Property>>> {
    const searchParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    // Always include page if not provided
    if (!searchParams.has('page')) {
      searchParams.append('page', '1');
    }
    
    const queryString = searchParams.toString();
    console.log('Fetching properties with params:', queryString);
    
    return this.request(`/properties.php${queryString ? `?${queryString}` : ''}`);
  }

  async testConnection(): Promise<ApiResponse<any>> {
    return this.request('/test.php');
  }

  async getProperty(id: number): Promise<ApiResponse<Property>> {
    return this.request(`/properties/${id}`);
  }

  async getPropertyBySlug(slug: string): Promise<ApiResponse<Property>> {
    return this.request(`/properties.php?slug=${slug}`);
  }

  async getFeaturedProperties(limit?: number): Promise<ApiResponse<Property[]>> {
    const queryString = limit ? `?type=featured&limit=${limit}` : '?type=featured';
    return this.request(`/properties.php${queryString}`);
  }

  async getPropertiesByCategory(categoryId: number, page?: number, perPage?: number): Promise<ApiResponse<PaginatedResponse<Property>>> {
    const params = new URLSearchParams();
    if (page) params.append('page', page.toString());
    if (perPage) params.append('per_page', perPage.toString());
    
    const queryString = params.toString();
    return this.request(`/properties/category/${categoryId}${queryString ? `?${queryString}` : ''}`);
  }

  async getPropertyStats(): Promise<ApiResponse<PropertyStats>> {
    return this.request('/properties/stats');
  }

  async createProperty(property: Partial<Property>): Promise<ApiResponse<Property>> {
    return this.request('/properties', {
      method: 'POST',
      body: JSON.stringify(property),
    });
  }

  async updateProperty(id: number, property: Partial<Property>): Promise<ApiResponse<Property>> {
    return this.request(`/properties/${id}`, {
      method: 'PUT',
      body: JSON.stringify(property),
    });
  }

  async deleteProperty(id: number): Promise<ApiResponse<void>> {
    return this.request(`/properties/${id}`, {
      method: 'DELETE',
    });
  }

  // Categories API
  async getCategories(): Promise<ApiResponse<Category[]>> {
    return this.request('/categories.php');
  }

  async getCategory(id: number): Promise<ApiResponse<Category>> {
    return this.request(`/categories/${id}`);
  }

  async getCategoryBySlug(slug: string): Promise<ApiResponse<Category>> {
    return this.request(`/categories/slug/${slug}`);
  }

  async createCategory(category: Partial<Category>): Promise<ApiResponse<Category>> {
    return this.request('/categories', {
      method: 'POST',
      body: JSON.stringify(category),
    });
  }

  async updateCategory(id: number, category: Partial<Category>): Promise<ApiResponse<Category>> {
    return this.request(`/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(category),
    });
  }

  async deleteCategory(id: number): Promise<ApiResponse<void>> {
    return this.request(`/categories/${id}`, {
      method: 'DELETE',
    });
  }

  async reorderCategories(categoryIds: number[]): Promise<ApiResponse<void>> {
    return this.request('/categories/reorder', {
      method: 'POST',
      body: JSON.stringify({ category_ids: categoryIds }),
    });
  }

  // Inquiries API
  async submitInquiry(inquiry: Partial<Inquiry>): Promise<ApiResponse<Inquiry>> {
    return this.request('/inquiries', {
      method: 'POST',
      body: JSON.stringify(inquiry),
    });
  }

  // Media Upload API
  async uploadPropertyImages(propertyId: number, files: FileList): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('property_id', propertyId.toString());
    
    Array.from(files).forEach((file, index) => {
      formData.append(`images[${index}]`, file);
    });

    return this.request('/properties/upload-images', {
      method: 'POST',
      headers: {}, // Remove Content-Type to let browser set it for FormData
      body: formData,
    });
  }

  async uploadPropertyVideo(propertyId: number, file: File): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('property_id', propertyId.toString());
    formData.append('video', file);

    return this.request('/properties/upload-video', {
      method: 'POST',
      headers: {}, // Remove Content-Type to let browser set it for FormData
      body: formData,
    });
  }

  async addYouTubeVideo(propertyId: number, youtubeUrl: string, title?: string): Promise<ApiResponse<any>> {
    return this.request('/properties/add-youtube-video', {
      method: 'POST',
      body: JSON.stringify({
        property_id: propertyId,
        youtube_url: youtubeUrl,
        title: title,
      }),
    });
  }

  // Brochures API
  async uploadBrochures(propertyId: number, files: FileList, brochureData?: Array<{title?: string, description?: string, sort_order?: number}>): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('property_id', propertyId.toString());
    
    for (let i = 0; i < files.length; i++) {
      formData.append('brochures[]', files[i]);
    }
    
    if (brochureData) {
      formData.append('brochure_data', JSON.stringify(brochureData));
    }

    return this.request('/brochures?path=upload', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set the Content-Type for FormData
    });
  }

  async getPropertyBrochures(propertyId: number): Promise<ApiResponse<any[]>> {
    return this.request(`/brochures?path=property&property_id=${propertyId}`);
  }

  async deleteBrochure(brochureId: number): Promise<ApiResponse<void>> {
    return this.request('/brochures', {
      method: 'DELETE',
      body: JSON.stringify({ id: brochureId }),
    });
  }

  getBrochureDownloadUrl(brochureId: number): string {
    return `${API_BASE_URL}/brochures.php?path=download&id=${brochureId}`;
  }

  getBrochureViewUrl(brochureId: number): string {
    return `${API_BASE_URL}/brochures.php?path=view&id=${brochureId}`;
  }

  async uploadPropertyBrochure(propertyId: number, file: File, title?: string): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('property_id', propertyId.toString());
    formData.append('brochures', file); // Changed to match backend expectation
    if (title) {
      formData.append('title', title);
    }

    return this.request('/brochures.php?path=upload', {
      method: 'POST',
      headers: {}, // Remove Content-Type to let browser set it for FormData
      body: formData,
    });
  }

  // Utility methods
  getImageUrl(imagePath: string): string {
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    // Remove /api to keep /backend prefix (files are stored in backend/uploads/)
    const baseUrl = API_BASE_URL.replace('/api', '');

    // Handle relative paths from database - add uploads/ prefix if not already there
    const fullPath = imagePath.startsWith('uploads/') ? imagePath : `uploads/${imagePath}`;

    return `${baseUrl}/${fullPath}`;
  }

  getThumbnailUrl(thumbnailPath: string): string {
    if (!thumbnailPath) return '';
    if (thumbnailPath.startsWith('http')) {
      return thumbnailPath;
    }
    return `${API_BASE_URL.replace('/api', '')}/uploads/${thumbnailPath}`;
  }

  getBrochureUrl(brochurePath: string): string {
    if (brochurePath.startsWith('http')) {
      return brochurePath;
    }
    // Remove any leading 'uploads/' to prevent duplication
    const cleanPath = brochurePath.replace(/^uploads\//, '');
    return `${API_BASE_URL.replace('/api', '')}/${cleanPath}`;
  }

  formatPrice(price: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  }

  formatArea(area: number, unit: string = 'sqft'): string {
    return `${area.toLocaleString()} ${unit}`;
  }

  // Site Settings API
  async getSiteSettings(): Promise<ApiResponse<any>> {
    return this.request('/site-settings.php');
  }

  async updateSiteSettings(settings: any): Promise<ApiResponse<any>> {
    return this.request('/site-settings.php', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  // Contact Form API
  async submitContactForm(formData: any): Promise<ApiResponse<any>> {
    return this.request('/contact-form.php', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  // Lonavala Investment Inquiry API
  async submitLonavalaInquiry(formData: any): Promise<ApiResponse<any>> {
    return this.request('/lonavala-inquiry.php', {
      method: 'POST',
      body: JSON.stringify(formData),
    });
  }

  // Policies API
  async getPolicy(slug: string): Promise<ApiResponse<any>> {
    return this.request(`/policies.php?path=get&slug=${slug}`);
  }

  // Legacy method for backward compatibility
  async getPolicyByType(type: 'terms_conditions' | 'refund_policy'): Promise<ApiResponse<any>> {
    return this.request(`/policies.php?path=get&type=${type}`);
  }

  async getAllPolicies(): Promise<ApiResponse<any[]>> {
    return this.request('/policies.php?path=list');
  }

  // Hero Slider API
  async getHeroSlides(): Promise<ApiResponse<any[]>> {
    return this.request('/hero-slider.php?path=list');
  }

  getHeroSlideUrl(imagePath: string): string {
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    return `${API_BASE_URL.replace('/api', '')}/${imagePath}`;
  }
}

export const apiService = new ApiService();
export default apiService;